<script setup lang="ts">
  import IconQC from '@qicgroupdoha/qic-connect-ds/components/IconQC'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  import TabsQC from '@qicgroupdoha/qic-connect-ds/components/TabsQC'
  import ModalQC from '@qicgroupdoha/qic-connect-ds/components/ModalQC'
  import { useI18n } from 'vue-i18n'

  import type { CoverModalData, CoverModalItem, CoverTab } from '../../FlowSelectPlanWidget.types'
  import { PLAN_NAMES_SCHEME_TYPE_CODE_MAP_V3 } from '@/shared/constants/plans.constants'
  import { SchemeType } from '@/shared/types/api.types'
  import { useGoogleAnalytics, useLinkTarget } from '@/shared/composables'
  import { onMounted } from 'vue'
  import { useRoute } from 'vue-router'
  import type { AvailableFlowRoutes } from '@/shared/types/flow.types'

  interface Props {
    data: CoverModalItem[]
    policyWordingLink: string
    withTabs?: boolean
  }

  interface Emits {
    (e: 'close'): void
  }

  const props = defineProps<Props>()

  const emit = defineEmits<Emits>()

  const activeTab = defineModel<string>()
  const { t, locale } = useI18n()
  const route = useRoute()
  const { getLinkTarget } = useLinkTarget()

  const tabs: CoverTab[] = [
    {
      label: t(PLAN_NAMES_SCHEME_TYPE_CODE_MAP_V3[SchemeType.Worldwide]),
      id: 'worldwideexcl',
    },
    { label: t(PLAN_NAMES_SCHEME_TYPE_CODE_MAP_V3[SchemeType.FlyEurope]), id: 'flyeurope' },
  ]

  const getVisibleCovers = (covers: CoverModalData[]) =>
    covers.filter((cover) => !cover?.hideInModal)
  const getVisibelData = (data: CoverModalItem[]) => data.filter((item) => !item?.hideInModal)

  const gaService = useGoogleAnalytics()

  onMounted(() => {
    gaService.sendPlanDetailsPopupOpened(route.name as AvailableFlowRoutes)
  })

  const handleTabClick = (tab: CoverTab) => {
    gaService.sendPlanTabClick(route.name as AvailableFlowRoutes, tab.id)
  }

  const handlePolicyWordingsClick = () => {
    gaService.sendOutboundPlanDetailsPolicyWordings()
  }
</script>

<template>
  <!-- @vue-ignore -->
  <ModalQC
    :title="$t('outbound.select_plan.insurance_values.pop_up.header_upd')"
    hide-controls
    position="side"
    :locale="locale"
    @close="emit('close')"
  >
    <template #content>
      <!-- @vue-ignore -->
      <TabsQC
        v-if="withTabs"
        v-model="activeTab"
        variant="solid"
        size="medium"
        data-test="covers-tabs"
        :tabs="tabs"
        class="covers-tabs"
        @change="handleTabClick"
      />

      <div class="covers-popup">
        <div
          v-for="item in getVisibelData(props.data)"
          :key="item.title"
          class="covers-popup__item"
        >
          <h1 class="heading-6 covers-popup__title">{{ $t(item.title) }}</h1>
          <div class="covers-popup__content">
            <div
              v-for="cover in getVisibleCovers(item.cover)"
              :key="cover.name"
              class="covers-popup__content-item"
            >
              <IconQC name="check" color="var(--colorIcnSrfAction)" size="lg" />
              <h2 class="text-medium">{{ $t(cover.name) }}</h2>
              <p class="text-small-medium covers-popup__content-price">{{ $t(cover.price) }}</p>
              <p
                v-if="typeof cover.description === 'string'"
                class="text-regular covers-popup__content-description"
              >
                {{ $t(cover.description) }}
              </p>
              <ul v-else class="covers-popup__content-description">
                <li
                  v-for="(description, descriptionIndex) in cover.description"
                  :key="descriptionIndex"
                  class="text-medium"
                >
                  <span
                    v-if="typeof description === 'object' && description?.title"
                    class="text-medium"
                    >{{ $t(description.title) }}</span
                  >
                  <span class="text-regular">{{
                    $t(typeof description === 'string' ? description : description.text)
                  }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <ButtonQC
          variant="secondary"
          icon-before-content="file"
          data-test="policy-wordings-link"
          :href="policyWordingLink"
          :target="getLinkTarget()"
          wide
          @click="handlePolicyWordingsClick"
        >
          {{ $t('outbound.plans.plan_information.policy_wording') }}
        </ButtonQC>
      </div>
    </template>
  </ModalQC>
</template>

<style scoped lang="scss">
  @use '@/shared/assets/styles/utils' as *;

  .covers-popup {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-5);
  }

  .covers-popup__item {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-5);
    background-color: var(--colorBgSrfSecondary);
    border-radius: var(--spacing-6);
    padding-bottom: var(--spacing-7);
  }

  .covers-popup__title {
    color: var(--colorTxtSrfPrimary);
    border-bottom: 1px solid var(--colorBrdrSrfSecondary);
    padding: var(--spacing-7);

    @include mobile-flow {
      padding: var(--spacing-5);
    }
  }

  .covers-popup__content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-3);
    padding-inline: var(--spacing-7);

    @include mobile-flow {
      padding-inline: var(--spacing-5);
    }
  }

  .covers-popup__content-item {
    display: grid;
    grid-template-columns: 24px 1fr auto;
    grid-template-rows: auto auto;
    gap: var(--spacing-1) var(--spacing-3);
    padding-block: var(--spacing-5);

    &:not(:last-child) {
      border-bottom: 1px solid var(--colorBrdrSrfSecondary);
    }

    .covers-popup__content-description {
      grid-column: 2 / -1;
      padding-inline-end: var(--spacing-6);
      color: var(--colorTxtSrfSecondary);

      li {
        list-style-type: square;
        padding-bottom: var(--spacing-3);
        margin-inline-start: var(--spacing-5);
      }
    }
  }

  .covers-popup__content-price {
    color: var(--colorTxtSrfAction);
    margin-inline-start: auto;
  }

  .covers-tabs {
    width: 100%;
    margin-bottom: var(--spacing-5);
  }
</style>
